/**
 * Route-based layout configuration.
 * Defines which UI elements (carousel, info panel, menu bar) should be visible per route.
 * 
 * Routes are matched by prefix (startsWith), so:
 * - '/auth' matches '/auth', '/auth/login', '/auth/register', etc.
 * - '/kontakt' matches '/kontakt', '/kontakt/123', etc.
 */
export interface RouteLayoutConfig {
	/** Hide the carousel on this route */
	hideCarousel?: boolean;
	/** Hide the info panel on this route */
	hideInfoPanel?: boolean;
	/** Hide the menu bar on this route */
	hideMenuBar?: boolean;
}

/**
 * Route prefixes mapped to their layout configuration.
 * Organized by feature area for better maintainability.
 */
export const ROUTE_METADATA: Record<string, RouteLayoutConfig> = {
	// Authentication routes
	'/auth': {
		hideCarousel: true,
		hideInfoPanel: true
	},

	// Member area
	'/member': {
		hideCarousel: true,
		hideInfoPanel: true
	},
	'/member-support': {
		hideCarousel: true
	},

	// Weather display (kiosk mode)
	'/wetter-aushang': {
		hideCarousel: true,
		hideInfoPanel: true,
		hideMenuBar: true
	},

	// Contact pages
	'/kontakt': {
		hideCarousel: true
	},

	// Reference/redirect pages
	'/ref': {
		hideCarousel: true
	},

	// Regatta sub-pages
	'/regatta/twilight-races': {
		hideCarousel: true
	},
	'/regatta/race-track': {
		hideCarousel: true
	},
	'/regatta/hall-of-fame': {
		hideCarousel: true
	},

	// Club pages
	'/club/ausbildung': {
		hideCarousel: true
	},

	// Media
	'/videos': {
		hideCarousel: true
	},

	// Boats
	'/boote/eissegeln': {
		hideCarousel: true
	},
	// Sommerfest registration
	'/sommerfest-registration': {
		hideCarousel: true
	},
} as const;

/**
 * Get layout configuration for a route by checking all prefix matches.
 * Returns the longest matching prefix configuration.
 * 
 * @example
 * getRouteLayout('/auth/login') → { hideCarousel: true, hideInfoPanel: true }
 * getRouteLayout('/kontakt/123') → { hideCarousel: true }
 * getRouteLayout('/home') → {}
 */
export function getRouteLayout(currentRoute: string): RouteLayoutConfig {
	// Find all matching route prefixes
	const matchingPrefixes = Object.keys(ROUTE_METADATA).filter(prefix =>
		currentRoute.startsWith(prefix)
	);

	// If no matches, return empty config (show everything)
	if (matchingPrefixes.length === 0) {
		return {};
	}

	// Use the longest matching prefix (most specific route)
	const longestMatch = matchingPrefixes.reduce((a, b) =>
		a.length > b.length ? a : b
	);

	return ROUTE_METADATA[longestMatch] || {};
}